import React from "react";
import { NumericFormat } from "react-number-format";
import { cn } from "@/lib/utils";

import { useTrackEvent } from "libs/analytics";

import * as EVENTS from "constants/Events";
import { useSelector } from "react-redux";
import {
  getConversation,
  WrappedInboxState,
} from "selectors/conversationInboxSelectors";
import { Interlocutor } from "types/conversationInbox";
import InboxPreferences from "@inbox/pages/conversation-page/inbox-preferences";
import {
  UnknownUserInfoIcon,
  UnknownUserTooltip,
} from "@inbox/shared/common-styles";

interface Props {
  conversationId: string;
  visible: boolean;
  top?: number;
  left?: number;
  interlocutor?: Partial<Interlocutor>;
}

export function SMUAccountSummary({
  conversationId,
  visible = false,
  top,
  left,
  interlocutor: originalInterlocutor,
}: Props): JSX.Element {
  const { interlocutor, isUnknownUser, unknownInfo } = useSMUInterloctorInfo(
    conversationId,
    originalInterlocutor
  );

  const { name, followersCount, followingMe, followingHim, handle, platform } =
    interlocutor;

  const accountHandle = InboxPreferences.getSmuAccountHandle(platform, handle);

  const userWarningTooltip = unknownInfo;
  const shouldShowDetails =
    InboxPreferences.getSumAccountAllowedShowDetails(platform) &&
    !isUnknownUser;

  return (
    <div
      className={cn(
        "transition-all duration-300 absolute z-10",
        visible ? "opacity-100 visible" : "opacity-0 invisible"
      )}
      style={{
        left: left ? `${left}px` : "0",
        top: top ? `${top}px` : "100%",
      }}
    >
      <div className="bg-inbox-surface-raised shadow-[2px_2px_10px_rgb(0_0_0/25%)] rounded-md px-[15px] py-[13px] self-start w-[235px]">
        <span
          className="absolute bg-inbox-surface-raised rounded-br-sm block top-[-7px] left-1/2 ml-[-6.85px] w-[13.7px] h-[13.7px] border-r border-b border-inbox-border-subtle"
          style={{ transform: "matrix(-0.72, -0.69, 0.72, -0.69, 0, 0)" }}
        />
        <div className="pb-2.5 border-b border-inbox-border flex justify-between items-center">
          <div className="w-[calc(100%-60px)]">
            <div className="font-medium text-sm leading-[17px] text-ink-primary overflow-hidden text-ellipsis line-clamp-2 max-h-12 w-full break-auto mb-1">
              <span>{name}</span>
              {isUnknownUser && <UnknownUserInfoIcon className="ml-1" />}
            </div>
            {accountHandle && (
              <div className="text-sm leading-[17px] text-ink-primary w-full text-ellipsis overflow-hidden">
                {`@${accountHandle}`}
              </div>
            )}
          </div>
          <div>
            <ProfileIconWrapper>
              <ProfileLink
                name={name}
                handle={handle}
                isUnknownUser={isUnknownUser}
              />
            </ProfileIconWrapper>
          </div>
        </div>
        <div className="pt-2">
          {shouldShowDetails ? (
            <>
              <span className="text-sm leading-[14px] text-ink-muted">
                <NumericFormat
                  data-testid="number-formatter"
                  value={followersCount}
                  displayType={"text"}
                  thousandSeparator={true}
                />{" "}
                followers
              </span>
              {(followingMe || followingHim) && (
                <div className="leading-[1.1] flex justify-between mt-[3px]">
                  {followingMe && (
                    <span className="text-sm leading-[14px] text-ink-muted font-[450]">
                      Follows You
                    </span>
                  )}
                  {followingMe && followingHim && (
                    <span className="text-sm text-ink-muted mx-[5px]">
                      {" "}
                      &bull;{" "}
                    </span>
                  )}
                  {followingHim && (
                    <span className="text-sm leading-[14px] text-ink-muted font-[450]">
                      Following
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <span className="text-sm leading-[14px] text-ink-muted">
              {userWarningTooltip || "Follow data not available"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export const ProfileLink = ({
  handle,
  isUnknownUser = false,
  name,
}: {
  handle?: string;
  isUnknownUser?: boolean;
  name?: string;
}): JSX.Element => {
  const trackEvent = useTrackEvent();

  const onHandleClick = () => {
    if (handle && !isUnknownUser) {
      trackEvent({ eventName: EVENTS.INBOX_PROFILE_LINK_CLICK });
      window.open(handle, "_blank");
    }
  };

  const profileIcon = (
    <i
      aria-disabled={isUnknownUser}
      aria-label={
        isUnknownUser
          ? "User profile unavailable"
          : `Open ${name || "user"}'s profile`
      }
      className={cn(
        "fa-solid fa-arrow-up-right-from-square text-sm text-accent-blurple cursor-pointer",
        (!handle || isUnknownUser) && "cursor-not-allowed text-ink-muted"
      )}
      onClick={onHandleClick}
    />
  );

  return isUnknownUser ? (
    <UnknownUserTooltip className="m-0">{profileIcon}</UnknownUserTooltip>
  ) : (
    profileIcon
  );
};

export function useSMUInterloctorInfo(
  conversationId: string,
  interlocutor?: Partial<Interlocutor>
) {
  const conversation = useSelector((state: WrappedInboxState) =>
    getConversation(state, conversationId)
  );

  interlocutor = interlocutor || conversation?.interlocutor || {};

  const isUnknownUser = Boolean(
    interlocutor.isUnknownSource || interlocutor.unknownUser
  );

  return {
    interlocutor,
    isUnknownUser,
    unknownInfo: isUnknownUser
      ? "Profile details aren't available for this user."
      : undefined,
  };
}

export const ProfileIconWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <div className="w-8 h-8 bg-accent-blurple-soft rounded-full flex items-center justify-center">
    {children}
  </div>
);
